import React, { useState, useEffect, useRef } from "react";
import {
  FiBookOpen,
  FiChevronDown,
  FiX,
  FiCheck,
  FiGrid,
  FiLayers,
  FiBook,
  FiUserCheck
} from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";

// Tabs
import ClassSectionTab from "./components/tab/ClassSectionTab";
import SubjectTab from "./components/tab/SubjectTab";
import SubjectAssignmentTab from "./components/tab/SubjectAssignmentTab";
import ClassroomTab from "./components/tab/ClassroomTab";

// Academic Dropdown Options
const ACADEMIC_OPTIONS = [
  { 
    id: "class_section", 
    label: "Class Section", 
    category: "Academic", 
    icon: FiGrid,
    description: "Manage and configure class sections, capacity, and home rooms."
  },
  { 
    id: "course_subject", 
    label: "Course & Subject", 
    category: "Curriculum", 
    icon: FiBook,
    description: "Maintain course catalogs, subject units, and syllabus details."
  },
  { 
    id: "subject_assignment", 
    label: "Subject Assignment", 
    category: "Allocation", 
    icon: FiUserCheck,
    description: "Assign subjects to teachers and allocate sections."
  },
  { 
    id: "setup_classroom", 
    label: "Setup Classroom", 
    category: "Setup", 
    icon: FiLayers,
    description: "Configure classroom environments, facilities, and seating layouts."
  },
];

export default function AcademicManager({ assignedClassesList = [] }) {
  // Navigation / Tabs State
  const [activeTabs, setActiveTabs] = useState([]);
  const [currentTabId, setCurrentTabId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Layout & Global State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Teacher", last_name: "User", email: "teacher@school.edu" });
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(false);

  const notificationDropdownRef = useRef(null);

  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");

    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (err) {
        console.error("Failed to parse permissions:", err);
      }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Teacher",
          last_name: userObj.last_name || "User",
          username: userObj.username || "teacher",
          email: userObj.email || "teacher@school.edu"
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const yearDisplay = "2025/2026";
  const termDisplay = "Term 1";

  // Handle selecting an item from the custom dropdown
  const handleSelectOption = (option) => {
    setIsDropdownOpen(false);

    if (!activeTabs.some((tab) => tab.id === option.id)) {
      setActiveTabs((prev) => [...prev, option]);
    }
    setCurrentTabId(option.id);
  };

  // Close an individual tab
  const handleCloseTab = (e, optionId) => {
    e.stopPropagation();
    const updatedTabs = activeTabs.filter((tab) => tab.id !== optionId);
    setActiveTabs(updatedTabs);

    if (currentTabId === optionId) {
      if (updatedTabs.length > 0) {
        setCurrentTabId(updatedTabs[updatedTabs.length - 1].id);
      } else {
        setCurrentTabId(null);
      }
    }
  };

  const activeOption = activeTabs.find((tab) => tab.id === currentTabId);

  // Helper function to render active component based on selected tab ID
  const renderTabContent = () => {
    if (!activeOption) return null;

    switch (activeOption.id) {
      case "class_section":
        return <ClassSectionTab />;
      case "course_subject":
        return <SubjectTab />;
      case "subject_assignment":
        return <SubjectAssignmentTab />;
      case "setup_classroom":
        return <ClassroomTab />;
      default:
        return (
          <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-zinc-200 rounded-lg text-zinc-400 space-y-2">
            <activeOption.icon className="w-10 h-10 text-zinc-300" />
            <p className="text-xs font-semibold text-zinc-600">
              {activeOption.label} Content Workspace
            </p>
            <p className="text-[11px] text-zinc-400 max-w-xs text-center">
              {activeOption.description}
            </p>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen w-full bg-zinc-50/50 overflow-hidden font-dynamic-title">
      {/* Fixed Sidebar */}
      <Sidebar
        className="no-print"
        permissions={permissions}
        user={user}
        teacherSubjects={assignedClassesList}
        handleLogout={handleLogout}
        onOpenChangePassword={() => setIsModalOpen(true)}
      />

      {/* Main Layout Container */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Top Header */}
        <Header
          loading={loadingPeriod}
          loadingPeriod={loadingPeriod}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto flex flex-col min-w-0 custom-scroll">
          {/* Page Sub-Header / Selector Bar */}
          <div className="p-4 bg-white border-b border-zinc-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
            <div>
              <h1 className="text-sm font-bold text-zinc-900 tracking-tight">Academic Manager</h1>
              <p className="text-[11px] text-zinc-400">
                Select an academic module to open and manage inside dynamic tabs.
              </p>
            </div>

            {/* Custom Dropdown */}
            <div className="relative w-full sm:w-64">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium"
              >
                <div className="flex items-center gap-2 truncate">
                  <FiBookOpen className="w-4 h-4 text-zinc-400 shrink-0" />
                  <span className="truncate">Select Academic Module...</span>
                </div>
                <FiChevronDown
                  className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Custom Dropdown Menu */}
              {isDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-30 max-h-60 overflow-y-auto custom-scroll">
                    <div className="px-3 py-1.5 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                      Academic Modules
                    </div>
                    {ACADEMIC_OPTIONS.map((option) => {
                      const isOpen = activeTabs.some((tab) => tab.id === option.id);
                      const IconComponent = option.icon;

                      return (
                        <button
                          key={option.id}
                          onClick={() => handleSelectOption(option)}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
                        >
                          <div className="flex items-center gap-2.5 min-w-0 pr-2">
                            <IconComponent className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-zinc-800 truncate">
                                {option.label}
                              </p>
                              <p className="text-[10px] text-zinc-400">{option.category}</p>
                            </div>
                          </div>
                          {isOpen && <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Dynamic Tabs Bar */}
          {activeTabs.length > 0 && (
            <div className="flex items-center gap-1 px-4 pt-2 bg-white border-b border-zinc-200/80 overflow-x-auto scrollbar-none shrink-0">
              {activeTabs.map((tab) => {
                const isActive = tab.id === currentTabId;
                const IconComponent = tab.icon;

                return (
                  <div
                    key={tab.id}
                    onClick={() => setCurrentTabId(tab.id)}
                    className={`group flex items-center gap-2 px-3 py-2 rounded-t-lg border-t border-x cursor-pointer transition-all text-xs font-semibold shrink-0 select-none ${
                      isActive
                        ? "bg-zinc-50 border-zinc-200/80 text-zinc-900 border-b-transparent -mb-px"
                        : "bg-zinc-100/60 border-transparent text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
                    }`}
                  >
                    <IconComponent
                      className={`w-3.5 h-3.5 ${isActive ? "text-zinc-700" : "text-zinc-400"}`}
                    />
                    <span>{tab.label}</span>
                    <button
                      onClick={(e) => handleCloseTab(e, tab.id)}
                      className="p-0.5 rounded-md hover:bg-zinc-200/80 text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Academic Workspace Panel */}
          <div className="flex-1 pl-6 pr-6 pb-6 pt-2">
            {activeOption ? (
              <div className="rounded-lg p-2 shadow-xs min-h-[400px]">
                {renderTabContent()}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 border border-dashed border-zinc-200 rounded-xl bg-white/50 text-center">
                <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-400 mb-3">
                  <FiBookOpen className="w-5 h-5" />
                </div>
                <p className="text-xs font-bold text-zinc-800">No Module Selected</p>
                <p className="text-[11px] text-zinc-400 max-w-xs mt-0.5">
                  Select an academic module from the custom dropdown menu above to view it in an active tab.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}